import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logSupabaseException } from "@punchup/sentry";
import {
  ArtistValueFeedbackRow,
  SubmitArtistValueFeedback,
} from "../../models/artist-value-feedback";

/**
 * Insert one "rate-the-moment" feedback row (PUN-5039).
 *
 * `userId` comes from the authenticated session at the route boundary, never
 * from the request body, so the stored row is always self-attributed. Reads
 * are handled separately by the super-admin dashboard fetcher.
 */
export async function insertArtistValueFeedback(
  supabase: SupabaseClient<Database>,
  userId: string,
  input: SubmitArtistValueFeedback
): Promise<ArtistValueFeedbackRow> {
  const { data, error } = await supabase
    .from("artist_value_feedback")
    .insert({
      user_id: userId,
      rating: input.rating,
      surface: input.surface,
      comment: input.comment ?? null,
      // context is free-form jsonb; the model keeps it ergonomic and we widen
      // to the generated Json shape only at the DB boundary.
      context: (input.context ??
        null) as Database["public"]["Tables"]["artist_value_feedback"]["Insert"]["context"],
    })
    .select("*")
    .single();

  if (error) {
    logSupabaseException(error, "insertArtistValueFeedback");
    throw error;
  }

  return data;
}

/**
 * Whether `adminId` manages `comedianId` (a row in `comedian_dashboard_admins`).
 *
 * Unlike the legacy `getIsUserAuthorizedToManageComedian`, which derives the user
 * from the Supabase client's session, this takes an explicit admin id — so a
 * service-role route can authorize the SESSION user its guard already resolved.
 * The feedback submit route uses it to reject feedback attributed to a comedian
 * the submitter doesn't manage, which would otherwise pollute the super-admin
 * dashboard and the Slack notification with a comedian the submitter never
 * managed (PUN-5039). Fail-closed: a lookup error logs and denies (returns
 * false), mirroring `getAuthorizedComedianIdsServer`.
 */
export async function getIsUserAuthorizedToManageComedianById(
  supabase: SupabaseClient<Database>,
  adminId: string,
  comedianId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from("comedian_dashboard_admins")
    .select("comedian_id")
    .eq("admin_id", adminId)
    .eq("comedian_id", comedianId)
    .maybeSingle();

  if (error) {
    logSupabaseException(error, "getIsUserAuthorizedToManageComedianById");
    return false;
  }

  return data !== null;
}

/** Human-readable context for a feedback row, for the Slack notification. */
export interface FeedbackSlackContext {
  comedianName: string | null;
  comedianSlug: string | null;
}

/**
 * Resolve the comedian name + slug behind a feedback row's `comedian_id`, so a
 * Slack notification can read as a name + page link rather than a UUID
 * (PUN-5039). Best-effort: a missing id or a lookup miss just leaves the fields
 * `null` — it never throws, because it runs in the submit path and must not fail
 * the submission.
 */
export async function getFeedbackSlackContext(
  supabase: SupabaseClient<Database>,
  comedianId: string | null | undefined
): Promise<FeedbackSlackContext> {
  if (!comedianId) return { comedianName: null, comedianSlug: null };

  const { data } = await supabase
    .from("comedians")
    .select("display_name, slug")
    .eq("id", comedianId)
    .maybeSingle();

  return {
    comedianName: data?.display_name ?? null,
    comedianSlug: data?.slug ?? null,
  };
}
